import {useEffect, useRef} from "react";
import {useReactFlow} from "@xyflow/react";
import {useSubflows} from "@/contexts/SubflowsContext";
import {useSave} from "@/contexts/SaveContext";
import {createDefaultNodes} from "@/utils/defaultFlowData";
import {FlowMode, SavingState} from "@/types/node";

/**
 * Hook that syncs selected subflow data with ReactFlow
 * When a subflow is selected, it updates the ReactFlow nodes and edges
 * When selectedSubflowId is null, it uses the main flow data
 */
export const useSubflowToReactFlow = () => {
  const {selectedSubflow, selectedSubflowId, selectMainFlow, mainFlow} =
    useSubflows();
  const {flowMode, savingState, setBlockSave} = useSave();
  const {setNodes, setEdges, fitView} = useReactFlow();
  const isUpdatingRef = useRef(false);
  const prevSubflowIdRef = useRef<string | null>(null);
  const prevFlowModeRef = useRef<FlowMode | null>(null);
  const prevVersionRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  // Ensure main flow is selected by default on mount
  useEffect(() => {
    if (selectedSubflowId === null) {
      selectMainFlow();
    }
  }, [selectedSubflowId, selectMainFlow]);

  useEffect(() => {
    // Skip if data is still loading from server
    if (savingState === SavingState.LOADING) {
      return;
    }

    // Get the current flow data to check version
    const currentFlowData = selectedSubflow?.flowData;
    const flowData =
      flowMode === FlowMode.LIVE
        ? currentFlowData?.live
        : currentFlowData?.test;
    const currentVersion = flowData?.version?.toString();

    // Only run if subflow changed, flow mode changed, or version changed
    const subflowChanged = prevSubflowIdRef.current !== selectedSubflowId;
    const flowModeChanged = prevFlowModeRef.current !== flowMode;
    const versionChanged = prevVersionRef.current !== currentVersion;

    // Skip if nothing changed (this prevents running during normal node edits)
    // But allow the first run after data is loaded
    if (
      !subflowChanged &&
      !flowModeChanged &&
      !versionChanged &&
      hasInitializedRef.current
    ) {
      return;
    }

    // Mark as initialized after first run
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
    }

    // Update refs
    prevSubflowIdRef.current = selectedSubflowId;
    prevFlowModeRef.current = flowMode;
    prevVersionRef.current = currentVersion;

    // Prevent race conditions by checking if update is already in progress
    if (isUpdatingRef.current) return;

    if ((selectedSubflowId === "main" || selectedSubflow) && flowData) {
      isUpdatingRef.current = true;

      const {nodes: currentNodes, edges: currentEdges} = flowData;

      // Nodes from context are already in blueprint format (settings as arrays)
      // No need to call restoreNodeSettings - that's only for processed nodes from storage
      const defaultNodes = createDefaultNodes();
      const nodesToRender =
        currentNodes.length > 0 ? currentNodes : defaultNodes;

      // Update ReactFlow with the current flow data
      // Clear first to force ReactFlow re-render, then set new data
      // CRITICAL: Block saves during this operation to prevent saving wrong canvas state
      setBlockSave(true);
      setNodes([]);
      setEdges([]);

      setTimeout(() => {
        setNodes(nodesToRender);
        setEdges(currentEdges);

        // Reset the flag and fit view
        setTimeout(() => {
          isUpdatingRef.current = false;
          setBlockSave(false);
          fitView({duration: 300});
        }, 50);
      }, 10);
    }
  }, [
    selectedSubflowId,
    flowMode,
    savingState,
    selectedSubflow,
    selectedSubflow?.flowData,
    mainFlow.flowData,
    setNodes,
    setEdges,
    fitView,
    setBlockSave,
  ]);
};
